import { FlowStatus, RouteFn, SubmitFn } from '@/hooks/use-flow';
import { SceneData } from '@/schemas/sceneDataSchema';
import { ErrorResponse } from '@/schemas/errorResponseSchema';
import { Error } from './Error';
import { flushSync } from 'react-dom';
import { useEffect, useState, useCallback, useRef } from 'react';
import { SkeletonCard } from './DynamicUI/SkeletonCard';
import { CenterColumnLayout, SceneWrap } from './SceneWrap';
import { useQuoteError } from '@/hooks/quote/use-quote-store';

interface FlowProps {
  sceneData?: SceneData;
  route: RouteFn;
  submit: SubmitFn;
  flowStatus: FlowStatus;
  errorData?: ErrorResponse | null;
  errorStatus?: number | null;
}

export const Flow = ({ flowStatus, route, submit, sceneData, errorData, errorStatus }: FlowProps) => {
  const quoteError = useQuoteError();

  const renderScene = useCallback(() => {
    if (quoteError) {
      return (
        <Error
          errorData={quoteError.data}
          errorStatus={quoteError.status}
        />
      );
    }

    switch (flowStatus) {
      case 'loading':
      case 'initializing':
        return (
          <CenterColumnLayout
            slotMain={<SkeletonCard />}
            backgroundColor='main'
          />
        );

      case 'ready':
        return (
          <SceneWrap
            sceneData={sceneData}
            route={route}
            submit={submit}
          />
        );

      case 'error':
        return (
          <Error
            errorData={errorData ?? undefined}
            errorStatus={errorStatus}
          />
        );

      default:
        return <Error />;
    }
  }, [flowStatus, sceneData, route, submit, errorData, errorStatus, quoteError]);

  const [currentScene, setCurrentScene] = useState(() => renderScene());
  const isTransitioning = useRef(false);

  const shouldSkipTransition = () => {
    if (!document.startViewTransition) return true; // Skip transition if View Transitions API is not supported
    if (document.visibilityState !== 'visible') return true; // Skip transition if document is not visible
    if (isTransitioning.current) return true; // Skip transition if one is already in progress
    return false;
  };

  useEffect(() => {
    const newScene = renderScene();

    if (shouldSkipTransition()) {
      // We'll eventually want to migrate to https://react.dev/reference/react/ViewTransition
      // Once that is stable and available
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentScene(newScene);
      return;
    }

    // Use View Transitions API to animate between scenes
    isTransitioning.current = true;
    const transition = document.startViewTransition(() => {
      flushSync(() => {
        setCurrentScene(newScene);
      });
    });

    // Cleanup transition state
    transition.finished
      .catch((error: Error) => {
        console.error('View transition error occurred:', {
          errorName: error.name,
          errorMessage: error.message,
          component: 'Flow.tsx',
          wasTransitioning: isTransitioning.current,
        });
      })
      .finally(() => {
        isTransitioning.current = false;
      });
  }, [renderScene]);

  return currentScene;
};
